import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { linkUserOnFirstLogin, subscribeUserProfile, ROLES } from '../services/userService';
import { activateTrial, getUserSubscription, getSystemConfig } from '../services/adminService';

const UserContext = createContext({});

export function UserProvider({ children }) {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [systemConfig, setSystemConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setProfile(null);
      setSubscription(null);
      setSystemConfig(null);
      setLoading(false);
      return;
    }

    let unsubscribe = () => {};

    const init = async () => {
      try {
        // 1. Carregar config do sistema
        let config;
        try {
          config = await getSystemConfig();
          setSystemConfig(config);
        } catch (err) {
          console.warn('Erro ao carregar config do sistema:', err);
          config = { trialAutomatico: true };
        }

        // 2. Vincular/criar perfil no primeiro login
        await linkUserOnFirstLogin(user);

        // 3. Verificar subscription e ativar trial se configurado
        try {
          const sub = await getUserSubscription(user.uid);
          if (!sub) {
            // Verificar se é super_admin antes de ativar trial
            const { getUserProfile } = await import('../services/userService');
            const tempProfile = await getUserProfile(user.uid);

            if (tempProfile?.role !== ROLES.SUPER_ADMIN) {
              // Só ativar trial se config.trialAutomatico estiver habilitado
              if (config?.trialAutomatico !== false) {
                await activateTrial(user.uid);
                const newSub = await getUserSubscription(user.uid);
                setSubscription(newSub);
              }
              // Se trial automático desabilitado, usuário fica sem plano
            }
          } else {
            setSubscription(sub);
          }
        } catch (err) {
          console.warn('Erro ao verificar/ativar trial:', err);
        }

        // 4. Escutar mudanças em tempo real
        unsubscribe = subscribeUserProfile(user.uid, (data) => {
          setProfile(data);
          setLoading(false);
        });
      } catch (err) {
        console.error('Erro ao inicializar perfil:', err);
        setLoading(false);
      }
    };

    init();
    return () => unsubscribe();
  }, [user]);

  // ── Flags de role ──
  const isSuperAdmin = profile?.role === ROLES.SUPER_ADMIN;
  const isMaster = profile?.role === ROLES.MASTER;
  const isGestor = profile?.role === ROLES.GESTOR;
  const isUsuario = profile?.role === ROLES.USUARIO;

  // ── Permissões de negócio ──
  const canManageUsers = isSuperAdmin || isMaster || isGestor;
  const canManageTeams = isSuperAdmin || isMaster || isGestor;
  const canManageGestores = isSuperAdmin || isMaster;
  const canViewAllData = isSuperAdmin || isMaster;
  const canManageWhiteLabel = isMaster;

  // ── Permissões de plataforma ──
  const canAccessAdmin = isSuperAdmin;
  const canManageBlog = isSuperAdmin;
  const canManagePlans = isSuperAdmin;
  const canManageSystemConfig = isSuperAdmin;
  const canManageAllUsers = isSuperAdmin;

  // ── Estado de manutenção ──
  const isMaintenanceMode = !!(systemConfig?.manutencao) && !isSuperAdmin;
  const maintenanceMessage = systemConfig?.manutencaoMsg || 'Sistema em manutenção. Voltamos em breve!';

  return (
    <UserContext.Provider value={{
      profile,
      loading,
      subscription,
      systemConfig,
      // Roles
      isSuperAdmin,
      isMaster,
      isGestor,
      isUsuario,
      // Permissões de negócio
      canManageUsers,
      canManageTeams,
      canManageGestores,
      canViewAllData,
      canManageWhiteLabel,
      // Permissões de plataforma
      canAccessAdmin,
      canManageBlog,
      canManagePlans,
      canManageSystemConfig,
      canManageAllUsers,
      // Manutenção
      isMaintenanceMode,
      maintenanceMessage,
    }}>
      {children}
    </UserContext.Provider>
  );
}

export const useUserProfile = () => useContext(UserContext);